import { KONKORD_ACCENT, TypeTheme } from "@revolt/state/stores/Theme";

import { konkordBackdrop } from "./backdrop";
import { generateMaterialYouScheme } from "./materialTheme";

/**
 * Konkord: options a theme preset sets (all of them, so picking a preset
 * also clears the gradient or colours of the previous one)
 */
export type ThemePresetColours = Pick<
  TypeTheme,
  | "mode"
  | "m3Accent"
  | "m3Contrast"
  | "m3Variant"
  | "m3Surface"
  | "surfaceTint"
  | "textColour"
  | "backdrop"
  | "backdropFrom"
  | "backdropTo"
  | "backdropAngle"
  | "backdropStrength"
  | "stars"
>;

/**
 * Konkord: a ready-made theme, applied through the regular colour options
 */
export type ThemePreset = {
  id: string;
  name: string;
  group: "basic" | "colour" | "gradient" | "light";
  colours: ThemePresetColours;
};

/**
 * Konkord: what a preset leaves unset
 */
const BASE: ThemePresetColours = {
  mode: "dark",
  m3Accent: KONKORD_ACCENT,
  m3Contrast: 0,
  m3Variant: "tonal_spot",
  m3Surface: "default",
  surfaceTint: null,
  textColour: null,
  backdrop: "none",
  backdropFrom: "#7c3aed",
  backdropTo: "#0ea5e9",
  backdropAngle: 135,
  backdropStrength: 35,
  stars: false,
};

function preset(
  id: string,
  name: string,
  group: ThemePreset["group"],
  colours: Partial<ThemePresetColours>,
): ThemePreset {
  return { id, name, group, colours: { ...BASE, ...colours } };
}

/**
 * Konkord: themes offered at the top of the appearance settings, in groups
 * (names are proper nouns, not translated)
 */
export const THEME_PRESETS: ThemePreset[] = [
  preset("konkord", "Automático", "basic", { mode: "system" }),
  preset("dark", "Escuro", "basic", {}),
  preset("light", "Claro", "basic", { mode: "light" }),
  preset("midnight", "Meia-noite", "basic", { m3Surface: "black" }),
  preset("discord", "Discórdia", "basic", {
    m3Accent: "#5865f2",
    m3Variant: "fidelity",
    m3Surface: "grey",
  }),
  preset("contrast", "Alto contraste", "basic", {
    m3Accent: "#ffd60a",
    m3Contrast: 1,
    m3Surface: "black",
    textColour: "#ffffff",
  }),

  preset("forest", "Floresta", "colour", {
    m3Accent: "#3fa34d",
    surfaceTint: "#1f5130",
  }),
  preset("ocean", "Oceano", "colour", {
    m3Accent: "#0288d1",
    surfaceTint: "#0b3a5c",
  }),
  preset("sakura", "Sakura", "colour", {
    m3Accent: "#f06292",
    surfaceTint: "#5c1f3a",
  }),
  preset("amber", "Âmbar", "colour", {
    m3Accent: "#ffa000",
    surfaceTint: "#4a3410",
  }),
  preset("coffee", "Café", "colour", {
    m3Accent: "#c08457",
    m3Surface: "grey",
    surfaceTint: "#6b4423",
    textColour: "#f1e4d6",
  }),
  preset("wine", "Vinho", "colour", {
    m3Accent: "#e11d48",
    surfaceTint: "#4c0519",
  }),

  preset("stellar", "Estelar", "gradient", {
    m3Accent: "#7c8cff",
    m3Surface: "space",
    stars: true,
    backdrop: "glow",
    backdropFrom: "#4f46e5",
    backdropTo: "#7e22ce",
    backdropStrength: 22,
  }),
  preset("aurora", "Aurora", "gradient", {
    m3Accent: "#34d399",
    backdrop: "aurora",
    backdropFrom: "#10b981",
    backdropTo: "#8b5cf6",
    backdropAngle: 160,
    backdropStrength: 34,
  }),
  preset("sunset", "Pôr do sol", "gradient", {
    m3Accent: "#fb923c",
    backdrop: "linear",
    backdropFrom: "#f97316",
    backdropTo: "#be185d",
    backdropAngle: 160,
    backdropStrength: 30,
  }),
  preset("nebula", "Nebulosa", "gradient", {
    m3Accent: "#c084fc",
    m3Surface: "space",
    surfaceTint: "#3b1a6b",
    stars: true,
    backdrop: "glow",
    backdropFrom: "#7c3aed",
    backdropTo: "#db2777",
    backdropStrength: 38,
  }),
  preset("deep", "Profundezas", "gradient", {
    m3Accent: "#22d3ee",
    backdrop: "linear",
    backdropFrom: "#0369a1",
    backdropTo: "#020617",
    backdropAngle: 180,
    backdropStrength: 45,
  }),
  preset("cyberpunk", "Cyberpunk", "gradient", {
    m3Accent: "#ff2bd6",
    m3Surface: "black",
    backdrop: "glow",
    backdropFrom: "#ff00a0",
    backdropTo: "#00e5ff",
    backdropStrength: 30,
  }),
  preset("volcano", "Vulcão", "gradient", {
    m3Accent: "#ef4444",
    backdrop: "aurora",
    backdropFrom: "#dc2626",
    backdropTo: "#f59e0b",
    backdropAngle: 20,
    backdropStrength: 28,
  }),

  preset("mint", "Menta", "light", {
    mode: "light",
    m3Accent: "#059669",
    backdrop: "linear",
    backdropFrom: "#6ee7b7",
    backdropTo: "#a5f3fc",
    backdropStrength: 30,
  }),
  preset("candy", "Algodão-doce", "light", {
    mode: "light",
    m3Accent: "#db2777",
    backdrop: "glow",
    backdropFrom: "#f9a8d4",
    backdropTo: "#93c5fd",
    backdropStrength: 45,
  }),
  preset("paper", "Papel", "light", {
    mode: "light",
    m3Accent: "#8a6d3b",
    m3Variant: "neutral",
    surfaceTint: "#efe6d2",
  }),
];

/**
 * Konkord: find the preset the current colour options match, if any
 * @param theme Current theme options
 * @returns Matching preset id, or undefined when customised
 */
export function matchThemePreset(theme: ThemePresetColours) {
  const same = (a: string | null, b: string | null) =>
    (a ?? "").toLowerCase() === (b ?? "").toLowerCase();

  return THEME_PRESETS.find(
    ({ colours }) =>
      colours.mode === theme.mode &&
      same(colours.m3Accent, theme.m3Accent) &&
      colours.m3Contrast.toFixed(1) === theme.m3Contrast.toFixed(1) &&
      colours.m3Variant === theme.m3Variant &&
      colours.m3Surface === theme.m3Surface &&
      same(colours.surfaceTint, theme.surfaceTint) &&
      same(colours.textColour, theme.textColour) &&
      colours.stars === theme.stars &&
      colours.backdrop === theme.backdrop &&
      // the gradient's details only matter when there is one
      (colours.backdrop === "none" ||
        (same(colours.backdropFrom, theme.backdropFrom) &&
          same(colours.backdropTo, theme.backdropTo) &&
          colours.backdropAngle === theme.backdropAngle &&
          colours.backdropStrength === theme.backdropStrength)),
  )?.id;
}

/**
 * Konkord: colours shown on a preset's card
 * @param colours Preset (or current) options
 * @param prefersDark Whether the system prefers dark mode ("system" presets)
 * @returns Background (may be a gradient), surface, accent and text colours
 */
export function previewThemePreset(
  colours: ThemePresetColours,
  prefersDark: boolean,
) {
  const darkMode =
    colours.mode === "dark" || (colours.mode === "system" && prefersDark);
  const scheme = generateMaterialYouScheme(
    colours.m3Accent,
    darkMode,
    colours.m3Contrast,
    colours.m3Variant,
    colours.m3Surface,
    colours.surfaceTint,
    colours.textColour,
  );
  const backdrop = konkordBackdrop(colours, darkMode);

  return {
    background: backdrop
      ? `${backdrop}, ${scheme["surface-container-lowest"]}`
      : scheme["surface-container-lowest"],
    surface: scheme["surface-container-high"],
    accent: scheme.primary,
    text: scheme["on-surface"],
  };
}
